import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserCacheLocation,
  loadExternalTokens,
} from '@azure/msal-browser';
import { from, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { msalAuthConfig } from './msal.config';
import {
  ChallengeResponse,
  InitiateResponse,
  SignInChallenge,
  SignInMethod,
  SignUpAttributes,
  SignUpContinueResponse,
  SocialIdentityProvider,
  TokenResponse,
  UserNotFoundError,
} from '../models/auth.model';
import { AppStorageService } from '../services/app-storage.service';
import { SecureStorageService } from '../services/secure-storage.service';

// Microsoft Entra External ID (CIAM) Native Authentication Service:
// Docs: https://learn.microsoft.com/en-us/entra/external-id/customers/concept-native-authentication
// API reference: https://learn.microsoft.com/en-us/entra/external-id/customers/reference-native-authentication-api
@Injectable({ providedIn: 'root' })
export class MsalAuthService {
  private readonly baseUrl: string;
  private readonly signupBaseUrl: string;
  private readonly MSAL_TOKEN_KEY = 'ppu_msal_tokens';
  // Sign-in offers 'password' so CIAM can pick it for accounts that have a
  // local password identity (store-review account); OTP-only users still get
  // 'oob'. Sign-up stays OTP-only.
  private readonly SIGN_IN_CHALLENGE_TYPES = 'password oob redirect';
  private readonly SIGN_UP_CHALLENGE_TYPES = 'oob redirect';
  private continuationToken: string | null = null;
  private signUpContinuationToken: string | null = null;
  private signInUsername: string | null = null;

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private appStorageService: AppStorageService,
    private secureStorageService: SecureStorageService
  ) {
    const { tenantId } = environment.msal;
    // Native calls CIAM directly - its HTTP client sends no Origin header, so
    // the native-auth token endpoint accepts it. Web goes through the /api/ciam
    // relay (SWA Managed Function in prod, proxy.conf.js during `ng serve`)
    // which strips Origin/Referer before forwarding - see api/src/functions/ciamProxy.ts.
    const host = Capacitor.isNativePlatform()
      ? `https://${tenantId}.ciamlogin.com/${tenantId}`
      : `/api/ciam/${tenantId}`;
    this.baseUrl = `${host}/oauth2/v2.0`;
    this.signupBaseUrl = `${host}/signup/v1.0`;
  }

  // Status and Account Info
  isLoggedIn(): boolean {
    return (
      this.msalService.instance.getAllAccounts().length > 0 ||
      this.msalService.instance.getActiveAccount() != null
    );
  }

  getAccount(): AccountInfo | null {
    return (
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null
    );
  }

  // Authentication Flows
  handleNativeRedirect(url: string): Observable<AuthenticationResult | null> {
    Browser.close().catch(() => {});

    let hash: string | undefined;
    try {
      const parsed = new URL(url);
      hash = parsed.hash || parsed.search || undefined;
    } catch {
      hash = undefined;
    }

    return from(
      this.msalService.instance.handleRedirectPromise(
        hash ? { hash } : undefined
      )
    ).pipe(
      switchMap(async (result) => {
        if (result?.account) {
          this.applyAuthResult(result);

          if (Capacitor.isNativePlatform()) {
            await this.secureStorageService.setSecureStorageData(
              this.MSAL_TOKEN_KEY,
              JSON.stringify(result)
            );
          }
        }
        return result;
      }),
      catchError((err) => {
        console.error('MsalAuthService - handleNativeRedirect error', err);
        return throwError(() => err);
      })
    );
  }

  async loginWithIdentityProvider(
    provider: SocialIdentityProvider
  ): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      const token = await firstValueFrom(this.acquireToken());
      if (token) return;

      // prompt=login: logout() only clears local state, so the browser keeps
      // its CIAM session cookie - without forcing a fresh sign-in, CIAM would
      // silently reuse it and sign the previous account straight back in.
      await this.msalService.instance.loginRedirect({
        scopes: environment.msal.scopes,
        extraQueryParameters: { domain_hint: provider },
        prompt: 'login',
      });
      // Resolves after Capacitor Browser opens; auth result arrives via handleNativeRedirect
      return;
    }

    // MSAL v5 can't tell when the user closes the popup - it just waits for the
    // redirect bridge until popupBridgeTimeout. Overriding lets a new attempt
    // cancel that stale wait (it rejects) instead of failing interaction_in_progress.
    const result = await this.msalService.instance.loginPopup({
      scopes: environment.msal.scopes,
      extraQueryParameters: { domain_hint: provider },
      prompt: 'login',
      overrideInteractionInProgress: true,
    });
    this.applyAuthResult(result);
  }

  async startSignIn(email: string): Promise<SignInChallenge> {
    this.continuationToken = null;
    this.signUpContinuationToken = null;
    this.signInUsername = email;

    const initiateToken = await this.initiate('/initiate', email);
    const { continuationToken, codeLength, method } =
      await this.requestChallenge('/challenge', initiateToken);
    this.continuationToken = continuationToken;

    return { method, codeLength };
  }

  async submitPassword(password: string): Promise<void> {
    if (!this.signInUsername) {
      throw Error('App.Errors.SessionExpired');
    }

    // The continuation token can't be relied on after a failed attempt, so a
    // retry after a wrong password starts a fresh initiate/challenge.
    let continuationToken = this.continuationToken;
    this.continuationToken = null;
    if (!continuationToken) {
      const initiateToken = await this.initiate(
        '/initiate',
        this.signInUsername
      );
      const challenge = await this.requestChallenge('/challenge', initiateToken);
      if (challenge.method !== 'password') {
        throw Error('App.Errors.SignInFailed');
      }
      continuationToken = challenge.continuationToken;
    }

    try {
      const tokenResponse = await this.requestToken(
        new HttpParams()
          .set('continuation_token', continuationToken)
          .set('grant_type', 'password')
          .set('password', password)
      );

      await this.loadIntoMsalCache(tokenResponse);
      this.signInUsername = null;
    } catch (err) {
      throw Error('App.Errors.InvalidPassword');
    }
  }

  async submitOtp(code: string): Promise<void> {
    if (this.signUpContinuationToken) {
      return this.submitSignUpOtp(code);
    }

    if (!this.continuationToken) {
      throw Error('App.Errors.SessionExpired');
    }

    try {
      const tokenResponse = await this.requestToken(
        new HttpParams()
          .set('continuation_token', this.continuationToken)
          .set('grant_type', 'oob')
          .set('oob', code)
      );

      await this.loadIntoMsalCache(tokenResponse);
      this.continuationToken = null;
    } catch (err) {
      throw Error('App.Errors.InvalidOtp');
    }
  }

  // Sign Up Flow
  async startSignUp(
    email: string,
    attributes: SignUpAttributes
  ): Promise<{ codeLength: number }> {
    this.continuationToken = null;
    this.signUpContinuationToken = null;

    const signupInitiateToken = await this.initiate(
      '/start',
      email,
      this.signupBaseUrl,
      attributes
    );
    const { continuationToken, codeLength } = await this.requestChallenge(
      '/challenge',
      signupInitiateToken,
      this.signupBaseUrl
    );
    this.signUpContinuationToken = continuationToken;

    return { codeLength };
  }

  private async submitSignUpOtp(code: string): Promise<void> {
    let continueResponse = await this.signUpContinue(
      this.signUpContinuationToken!,
      new HttpParams().set('grant_type', 'oob').set('oob', code)
    );

    if (continueResponse.error === 'attributes_required') {
      throw Error('App.Errors.AdditionalInfoRequired');
    }

    if (continueResponse.error === 'invalid_grant') {
      throw Error('App.Errors.InvalidOtp');
    }

    if (!continueResponse.continuation_token) {
      // TODO: Translate this message
      throw Error('App.Errors.SignUpFailed');
    }

    try {
      const tokenResponse = await this.requestToken(
        new HttpParams()
          .set('continuation_token', continueResponse.continuation_token)
          .set('grant_type', 'continuation_token')
      );
      await this.loadIntoMsalCache(tokenResponse);
      this.signUpContinuationToken = null;
    } catch (err) {
      throw Error('App.Errors.SignUpFailed');
    }
  }

  // Token Management
  acquireToken(forceRefresh: boolean = false): Observable<string | null> {
    if (!forceRefresh) {
      const nowSeconds = Date.now() / 1000;
      const accessToken = this.appStorageService.accessToken();
      if (
        accessToken &&
        nowSeconds < this.appStorageService.accessTokenExpiresOn() - 60
      ) {
        return of(accessToken);
      }
    }

    return from(this.acquireTokenViaRefreshOrSilent(forceRefresh));
  }

  private async acquireTokenViaRefreshOrSilent(
    forceRefresh: boolean = false
  ): Promise<string | null> {
    const refreshToken = await this.getPersistedRefreshToken();
    if (refreshToken) {
      return this.refreshAccessToken(refreshToken);
    }

    const account = this.getAccount();
    if (!account) return null;

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: environment.msal.scopes,
        account,
        forceRefresh,
      });
      this.applyAuthResult(result);
      return result.accessToken;
    } catch (err) {
      if (forceRefresh) {
        console.error(
          'MsalAuthService - forced silent token refresh failed',
          err
        );
      }
      return null;
    }
  }

  private async getPersistedRefreshToken(): Promise<string | null> {
    const raw = await this.secureStorageService.getSecureStorageData(
      this.MSAL_TOKEN_KEY
    );
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed.refresh_token ?? null;
    } catch {
      return null;
    }
  }

  private async refreshAccessToken(
    refreshToken: string
  ): Promise<string | null> {
    try {
      const tokenResponse = await this.requestToken(
        new HttpParams()
          .set('grant_type', 'refresh_token')
          .set('refresh_token', refreshToken)
      );
      await this.loadIntoMsalCache(tokenResponse);
      return tokenResponse.access_token;
    } catch (err) {
      console.error('MsalAuthService - refreshAccessToken failed', err);
      await this.secureStorageService.removeSecureStorageData(
        this.MSAL_TOKEN_KEY
      );
      return null;
    }
  }

  async restoreMsalCache(): Promise<void> {
    const raw = await this.secureStorageService.getSecureStorageData(
      this.MSAL_TOKEN_KEY
    );
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if ('accessToken' in parsed) {
        await this.restoreFromAuthResult(parsed);
      } else {
        await this.loadIntoMsalCache(parsed as TokenResponse);
      }
    } catch (e) {
      console.error('MsalAuthService - restoreMsalCache failed', e);
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await this.msalService.instance.clearCache();
      this.appStorageService.accessToken.set(null);
      await this.secureStorageService.removeSecureStorageData(
        this.MSAL_TOKEN_KEY
      );
    } catch {}
  }

  // Helper Methods
  private async initiate(
    path: string,
    username: string,
    baseUrl = this.baseUrl,
    attributes?: SignUpAttributes
  ): Promise<string> {
    let body = new HttpParams()
      .set('client_id', environment.msal.clientId)
      .set('challenge_type', this.challengeTypesFor(baseUrl))
      .set('username', username);

    if (attributes) {
      body = body.set(
        'attributes',
        JSON.stringify({
          displayName: attributes.displayName,
          jobTitle: 'Customer',
        })
      );
    }

    const data = await this.post<InitiateResponse>(path, body, baseUrl);

    if (
      path === '/initiate' &&
      (data.error === 'user_not_found' || data.suberror === 'user_not_found')
    ) {
      // This Exception is used to trigger the Sign-Up flow from the Login page, so it is not translated and should be handled by the caller
      throw new UserNotFoundError(
        `${data.error ?? data.suberror} - ${data.error_description}`
      );
    }
    if (data.error || !data.continuation_token) {
      console.error(
        data.error
          ? `${data.suberror ?? data.error} - ${data.error_description}`
          : 'UNKNOWN - Unexpected response from sign-in service.'
      );
      throw Error('App.Errors.SignInFailed');
    }

    return data.continuation_token;
  }

  private async requestChallenge(
    path: string,
    continuationToken: string,
    baseUrl = this.baseUrl
  ): Promise<{
    continuationToken: string;
    codeLength: number;
    method: SignInMethod;
  }> {
    const body = new HttpParams()
      .set('client_id', environment.msal.clientId)
      .set('challenge_type', this.challengeTypesFor(baseUrl))
      .set('continuation_token', continuationToken);

    const data = await this.post<ChallengeResponse>(path, body, baseUrl);

    const method: SignInMethod | null = data.challenge_type?.includes('oob')
      ? 'oob'
      : data.challenge_type === 'password'
        ? 'password'
        : null;

    if (data.error || !method || !data.continuation_token) {
      console.error(
        data.error
          ? `${data.suberror ?? data.error} - ${data.error_description}`
          : 'UNKNOWN - Unexpected response from sign-in service.'
      );
      throw Error(
        baseUrl === this.signupBaseUrl
          ? 'App.Errors.SignUpFailed'
          : 'App.Errors.SignInFailed'
      );
    }

    return {
      continuationToken: data.continuation_token,
      codeLength: data.code_length ?? 8,
      method,
    };
  }

  private challengeTypesFor(baseUrl: string): string {
    return baseUrl === this.signupBaseUrl
      ? this.SIGN_UP_CHALLENGE_TYPES
      : this.SIGN_IN_CHALLENGE_TYPES;
  }

  private async signUpContinue(
    continuationToken: string,
    payload: HttpParams
  ): Promise<SignUpContinueResponse> {
    const body = payload
      .set('client_id', environment.msal.clientId)
      .set('continuation_token', continuationToken);

    try {
      const data = await this.post<SignUpContinueResponse>(
        '/continue',
        body,
        this.signupBaseUrl
      );

      if (
        data.error &&
        !(
          data.error === 'attributes_required' || data.error === 'invalid_grant'
        )
      ) {
        throw Error('App.Errors.SignUpFailed');
      }

      return data;
    } catch (err) {
      throw Error('App.Errors.SignUpFailed');
    }
  }

  private async requestToken(extraParams: HttpParams): Promise<TokenResponse> {
    const scopes = environment.msal.scopes.join(' ');
    const redirectUri = Capacitor.isNativePlatform()
      ? Capacitor.getPlatform() === 'android'
        ? environment.msal.androidNativeRedirectUri
        : environment.msal.nativeRedirectUri
      : window.location.origin;

    const body = extraParams
      .set('client_id', environment.msal.clientId)
      .set('scope', scopes)
      .set('redirect_uri', redirectUri);

    const data = await this.post<TokenResponse>('/token', body);

    if (data.error || !data.access_token) {
      console.error(
        data.error
          ? `${data.suberror ?? data.error} - ${data.error_description}`
          : 'UNKNOWN - Authentication failed. Please try again.'
      );
      throw Error(data.error);
    }

    return data;
  }

  private async restoreFromAuthResult(
    parsed: Record<string, unknown>
  ): Promise<void> {
    const rawAccount = parsed['account'] as Record<string, unknown> | undefined;
    if (
      rawAccount &&
      rawAccount['tenantProfiles'] &&
      !(rawAccount['tenantProfiles'] instanceof Map)
    ) {
      rawAccount['tenantProfiles'] = new Map(
        Object.entries(rawAccount['tenantProfiles'] as Record<string, unknown>)
      );
    }

    const authResult: AuthenticationResult = {
      ...(parsed as AuthenticationResult),
      expiresOn: parsed['expiresOn']
        ? new Date(parsed['expiresOn'] as string)
        : new Date(),
      extExpiresOn: parsed['extExpiresOn']
        ? new Date(parsed['extExpiresOn'] as string)
        : undefined,
    };
    const request = {
      authority: msalAuthConfig.authority,
      scopes: authResult.scopes,
      correlationId: crypto.randomUUID(),
    };
    await this.msalService.instance.hydrateCache(authResult, request);
    // No refresh_token available here (MSAL keeps it internal to interactive
    // flows) — acquireToken() falls back to acquireTokenSilent for this account.
    this.applyAuthResult(authResult);
  }

  private async loadIntoMsalCache(response: TokenResponse): Promise<void> {
    const nowSeconds = Math.round(Date.now() / 1000);
    const request = {
      authority: msalAuthConfig.authority,
      scopes: environment.msal.scopes,
      correlationId: crypto.randomUUID(),
    };

    const result = await loadExternalTokens(
      {
        auth: msalAuthConfig,
        cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
      },
      request,
      {
        token_type: 'Bearer',
        scope: response.scope ?? environment.msal.scopes.join(' '),
        expires_in: response.expires_in,
        access_token: response.access_token,
        id_token: response.id_token,
        refresh_token: response.refresh_token,
        client_info: response.client_info,
      },
      {
        expiresOn: nowSeconds + response.expires_in,
        extendedExpiresOn:
          nowSeconds + (response.ext_expires_in ?? response.expires_in),
      }
    );

    await this.msalService.instance.hydrateCache(result, request);
    this.applyAuthResult(result);

    await this.secureStorageService.setSecureStorageData(
      this.MSAL_TOKEN_KEY,
      JSON.stringify(response)
    );
  }

  // Common tail of every flow that produces a fresh AuthenticationResult
  // (native redirect, Google popup, cache restore, CIAM token load/refresh):
  // activate the account, cache the access token, and derive the profile
  // from its already-decoded idTokenClaims.
  private applyAuthResult(result: AuthenticationResult): void {
    if (!result.account) return;
    this.msalService.instance.setActiveAccount(result.account);
    this.appStorageService.accessToken.set(result.accessToken);
    const claims = result.idTokenClaims as Record<string, unknown> | undefined;
    this.loadCustomerProfile(claims, result.account);
  }

  private loadCustomerProfile(
    claims?: Record<string, unknown> | undefined,
    account?: AccountInfo | null
  ): void {
    if (!account) account = this.getAccount();

    if (!account && !claims) return;

    claims =
      claims || (account!.idTokenClaims as Record<string, unknown> | undefined);
    const userId = (account?.idTokenClaims?.['userId'] as string) ?? null;
    const emailAddress =
      (claims?.['emailAddress'] as string) ?? account!.username ?? '';
    this.appStorageService.customerEmailAddress.set(emailAddress);
    if (userId) {
      this.appStorageService.setCustomerProfile({
        emailAddress: emailAddress,
        userId,
      });
    }
  }

  private async post<T>(
    path: string,
    body: HttpParams,
    baseUrl = this.baseUrl
  ): Promise<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    try {
      return await firstValueFrom(
        this.http.post<T>(`${baseUrl}${path}`, body.toString(), {
          headers,
        })
      );
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.error?.error) {
        if (err.error.error === 'user_not_found') {
          throw new UserNotFoundError(
            `${err.error.error} - ${err.error.error_description}`
          );
        }
        if (err.error.error_description?.includes('AADSTS1003037')) {
          // Account was created via Google or Apple SSO; Entra rejects OTP for it.
          // Checked ahead of the invalid_grant branch below, since Entra
          // reports this as an invalid_grant-shaped rejection too.
          throw Error('App.Errors.AccountExistsWithDifferentProvider');
        }
        if (
          err.error.error === 'attributes_required' ||
          err.error.error === 'invalid_grant'
        ) {
          return err.error as T;
        }
        console.error(`${err.error.error} - ${err.error.error_description}`);
        throw Error('App.Errors.AuthenticationFailed');
      }
      throw err;
    }
  }
}
